import React, { useContext, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { AnonymousOrderLookupFormDisplay } from './AnonymousOrderLookupFormDisplay';
import AnonErrorHandler from '../../shared/AnonErrorHandler';
import useSiteData from '../../../hooks/useSiteData';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import { OrderDetailsDisplay } from '../OrderDetailsDisplay';
import DebugTools from '../../debugTooling/DebugTools';
import { DebugModeContext } from '../../contexts/DebugModeContext';
import useIdentityContext from '../../../hooks/useIdentityContext';
import { formatFractionDigits } from '../../../util/fractionDigitsFormatter';
import {
  GET_ANONYMOUS_ORDER_DETAILS,
  GET_ANONYMOUS_ORDER_FEEDBACK_GROUPS,
} from '../../../constants/reactQueryKeys';
import { pageViewFindMyOrder } from '../../../apis/trackingApi';
import { getAnonymousOrderDetails } from '../../../apis/bopApi';
import { getTaxInclusiveAndExclusivePrices } from '../../../util/taxDisplayPricingHelpers';
import { customerFeedbackGroupStatesFilter } from '../../../constants/customerFeedbackGroupStates';
import { customerFeedbackTypesFilter } from '../../../constants/customerFeedbackTypes';
import { getFeedbackGroupsForAnonymousOrder } from '../../../apis/orderUpdateRequestsApi';
import { CustomerFeedbackGroupsProvider } from '../../contexts/CustomerFeedbackGroupsProvider';
import { BasicPreloader } from '../../shared/BasicPreloader';

/*
  Fetch and display order details for anonymously placed orders
*/
export const AnonymousOrderDetailsApplication = ({ orderIdOnPath }) => {
  const { auth, identity } = useIdentityContext();
  const { anonymousUserId } = identity;

  const { debugEnabled } = useContext(DebugModeContext);
  const { tenantEnvironment, vistacartTenant } = useSiteData();
  const [email, setEmail] = useState('');
  const [orderNumber, setOrderNumber] = useState(orderIdOnPath);

  useEffect(() => {
    pageViewFindMyOrder(anonymousUserId);
  }, [anonymousUserId]);

  const {
    isError,
    isLoading,
    data: order,
    error,
  } = useQuery(
    [GET_ANONYMOUS_ORDER_DETAILS, orderNumber],
    () =>
      getAnonymousOrderDetails({
        email,
        orderNumber,
        environment: tenantEnvironment,
        vistacartTenant,
      }),
    {
      // The query will not execute until the user email exists
      enabled: !!email,
    }
  );

  const { data: feedbackGroups } = useQuery(
    [GET_ANONYMOUS_ORDER_FEEDBACK_GROUPS, orderNumber, email],
    async () =>
      getFeedbackGroupsForAnonymousOrder({
        orderNumber,
        email,
        states: customerFeedbackGroupStatesFilter,
        types: customerFeedbackTypesFilter,
        environment: tenantEnvironment,
      }) || [],
    {
      enabled: !!orderNumber && !!email,
    }
  );

  // Prompt user with order lookup form until fetch is complete
  const displayOrderLookupForm = !order;

  const shopperId = order?.shopperId;
  const orderId = order?.orderNumber;
  const orderPlacedAnonymously = order?.orderPlacedAnonymously;

  const signInAndRedirectToOrderDetails = orderNum => {
    // push OD with orderNum to history stack so redirect to OD for orderNum after sign-in
    if (orderNum) {
      const url = new URL(window.location);
      url.searchParams.set('orderId', orderNum);
      window.history.pushState({}, '', url);
    }
    auth.signIn();
  };

  const fractionDigits = formatFractionDigits(get(order, 'fractionalDigits'));

  /* Bucket lineItem prices into itemPrices, shippingPrices, and itemGroupPrices and with tax exclusive / inclusive price arrays.
   * These maps of tax-inclusive/exclusive pricing objects are stored in OrderDetailsContext and used as formatted input for tax aware price display */
  const {
    itemIdToTaxInclusiveAndExclusivePricesMap,
    itemGroupIdToTaxInclusiveAndExclusivePricesMap,
    itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
  } = getTaxInclusiveAndExclusivePrices(order);

  const orderDetailsContextValues = useMemo(
    () => ({
      orderId,
      vistacartTenantName: vistacartTenant,
      shopperId,
      tenantEnvironment,
      currency: get(order, 'currency') || 'EUR',
      fractionDigits,
      order,
      itemIdToTaxInclusiveAndExclusivePricesMap,
      itemGroupIdToTaxInclusiveAndExclusivePricesMap,
      itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
    }),
    [
      fractionDigits,
      itemGroupIdToTaxInclusiveAndExclusivePricesMap,
      itemIdToTaxInclusiveAndExclusivePricesMap,
      order,
      orderId,
      shopperId,
      itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
      tenantEnvironment,
      vistacartTenant,
    ]
  );

  if (isLoading) {
    return <BasicPreloader isCentered />;
  }

  if (isError) {
    return <AnonErrorHandler error={error} />;
  }

  if (displayOrderLookupForm) {
    return (
      <AnonymousOrderLookupFormDisplay
        setEmail={setEmail}
        setOrderNumber={setOrderNumber}
        orderNumber={orderNumber}
      />
    );
  }

  // if the fetched order was not placed anonymously, then we need to force this user to login to view it.
  if (!orderPlacedAnonymously) {
    signInAndRedirectToOrderDetails(orderId);
    return null;
  }

  return (
    <CustomerFeedbackGroupsProvider feedbackGroups={feedbackGroups}>
      <OrderDetailsContext.Provider value={orderDetailsContextValues}>
        {debugEnabled && <DebugTools shopperId={shopperId} />}
        <OrderDetailsDisplay />
      </OrderDetailsContext.Provider>
    </CustomerFeedbackGroupsProvider>
  );
};

AnonymousOrderDetailsApplication.propTypes = {
  orderIdOnPath: PropTypes.string,
};
